import { all, call, fork, put, takeLatest, select } from "@redux-saga/core/effects";
import { message, Alert } from "antd";
import { ROLE_GUEST, UNPROCESSABLE_ENTITY_CODE } from "configs/AppConfig";
import { env } from "configs/EnvironmentConfig";
import {
  FETCH_LOCATION_LOOKUP_REQUEST,
  FETCH_ZONE_REQUEST,
  FETCH_WOREDA_REQUEST,
  FETCH_NEW_ORDER_TESTS_REQUEST,
  FETCH_EXISTING_NEW_ORDER_PATIENT_REQUEST,
  UPDATE_NEW_ORDER_REQUEST,
  CREATE_NEW_ORDER_REQUEST,
  CREATE_NEW_ORDER_BY_DOCTOR_REQUEST,
  UPDATE_CS_OR_FS_NUMBER_REQUEST,
  FETCH_DISCOUNT_PREVIEW_REQUEST
} from "constants/NewOrder";
import {
  fetchNewOrderTestSuccess,
  fetchNewOrderTestFailure,
  fetchLocationLookUpSuccess,
  fetchLocationLookUpFailure,
  setNewOrderValidationErrors,
  fetchNewOrderPatientsSuccess,
  fetchNewOrderPatientsFailure,
  updateNewOrderSuccess,
  updateNewOrderFailure,
  createNewOrderSuccess,
  createNewOrderFailure,
  fetchzoneSuccess,
  fetchzoneFailure,
  fetchworedaSuccess,
  fetchworedaFailure,
  updateCsOrFsNumberSuccess,
  updateCsOrFsNumberFailure,
  createNewOrderByDoctorSuccess,
  fetchDiscountPreviewSuccess,
  fetchDiscountPreviewFailure,
  discountLimitReached,
} from "redux/actions";

import NewOrderService from "services/NewOrder";
import { buildDiscountPreviewFingerprint } from "utils/discountPreviewSignature";
import { doesThisUserIsSrd, getCurrentRole } from "utils/CurrentUserUtil";
import { sendSampleToSampleManagement } from "utils/SampleManagement";
import { openPdf } from "views/admin-views/neworder/openPatientOrderSummary";
// import { sendSampleToSampleManagement } from "utils/SampleManagement";

const NEW_ORDER_TESTS_CACHE_KEY = "new-order-tests-directory";

const readCachedNewOrderTests = () => {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const raw = window.sessionStorage.getItem(NEW_ORDER_TESTS_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    window.sessionStorage.removeItem(NEW_ORDER_TESTS_CACHE_KEY);
    return null;
  }
};

const writeCachedNewOrderTests = (data) => {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.sessionStorage.setItem(NEW_ORDER_TESTS_CACHE_KEY, JSON.stringify(data));
  } catch (error) {
    // noop: cache is best-effort
  }
};

const clearCachedNewOrderTests = () => {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.sessionStorage.removeItem(NEW_ORDER_TESTS_CACHE_KEY);
  } catch (error) {
    // noop: cache is best-effort
  }
};

const DISCOUNT_PREVIEW_CHUNK_SIZE = 200;

const chunkPanelsForPreview = (panels, chunkSize = DISCOUNT_PREVIEW_CHUNK_SIZE) => {
  if (!Array.isArray(panels) || panels.length === 0) {
    return [];
  }

  const effectiveSize = Math.max(1, chunkSize);
  const batches = [];
  for (let index = 0; index < panels.length; index += effectiveSize) {
    batches.push(panels.slice(index, index + effectiveSize));
  }
  return batches;
};

const roundCurrency = (value) => {
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return 0;
  }
  return Math.round((numericValue + Number.EPSILON) * 100) / 100;
};

const accumulateAppliedDiscounts = (store, entries = []) => {
  if (!Array.isArray(entries) || entries.length === 0) {
    return store;
  }

  entries.forEach((entry) => {
    if (!entry) {
      return;
    }
    const key = `${entry.discount_id ?? "unknown"}:${entry.coupon_code ?? "none"}`;
    if (!store[key]) {
      store[key] = {
        ...entry,
        total_amount: roundCurrency(entry.total_amount),
      };
    } else {
      store[key] = {
        ...store[key],
        total_amount: roundCurrency((store[key].total_amount || 0) + (entry.total_amount || 0)),
      };
    }
  });

  return store;
};

const mergeCollectionsByKey = (store, entries = [], keySelector) => {
  if (!Array.isArray(entries) || typeof keySelector !== "function") {
    return store;
  }

  entries.forEach((entry) => {
    if (!entry) {
      return;
    }
    const key = keySelector(entry) || JSON.stringify(entry);
    if (!key) {
      return;
    }
    store[key] = entry;
  });

  return store;
};

const mergeDiscountPreviewResponses = (responses = [], totalPanels = 0) => {
  if (!Array.isArray(responses) || responses.length === 0) {
    return {
      panels: [],
      panel_map: {},
      summary: {
        subtotal_before_discount: 0,
        subtotal_after_discount: 0,
        extra_fees: 0,
        total_discount: 0,
        grand_total: 0,
      },
      applications: [],
      applied_discounts: [],
      coupon_models: [],
      coupon_feedback: [],
      __chunks: 0,
      __panels_processed: totalPanels,
    };
  }

  const mergedPanels = [];
  const mergedPanelMap = {};
  const mergedApplications = [];
  const appliedDiscountAccumulator = {};
  const couponModelMap = {};
  const couponFeedbackMap = {};
  let subtotalBefore = 0;
  let subtotalAfter = 0;
  let totalDiscount = 0;
  let extraFees = null;

  responses.forEach((response) => {
    if (!response) {
      return;
    }

    if (Array.isArray(response.panels)) {
      mergedPanels.push(...response.panels);
    }

    Object.assign(mergedPanelMap, response.panel_map || {});

    if (Array.isArray(response.applications)) {
      mergedApplications.push(...response.applications);
    }

    accumulateAppliedDiscounts(appliedDiscountAccumulator, response.applied_discounts);
    mergeCollectionsByKey(couponModelMap, response.coupon_models, (model) => model?.code || model?.id);
    mergeCollectionsByKey(
      couponFeedbackMap,
      response.coupon_feedback,
      (feedback) => feedback?.code || `${feedback?.status}:${feedback?.message}`
    );

    const summary = response.summary || {};
    subtotalBefore += Number(summary.subtotal_before_discount) || 0;
    subtotalAfter += Number(summary.subtotal_after_discount) || 0;
    totalDiscount += Number(summary.total_discount) || 0;
    if (extraFees === null && typeof summary.extra_fees === "number") {
      extraFees = Number(summary.extra_fees) || 0;
    }
  });

  const normalizedSummary = {
    subtotal_before_discount: roundCurrency(subtotalBefore),
    subtotal_after_discount: roundCurrency(subtotalAfter),
    extra_fees: roundCurrency(extraFees ?? 0),
    total_discount: roundCurrency(totalDiscount || subtotalBefore - subtotalAfter),
    grand_total: roundCurrency(subtotalAfter + (extraFees ?? 0)),
  };

  return {
    panels: mergedPanels,
    panel_map: mergedPanelMap,
    summary: normalizedSummary,
    applications: mergedApplications,
    applied_discounts: Object.values(appliedDiscountAccumulator),
    coupon_models: Object.values(couponModelMap),
    coupon_feedback: Object.values(couponFeedbackMap),
    __chunks: responses.length,
    __panels_processed: totalPanels,
  };
};

const DISCOUNT_PREVIEW_CACHE_STORAGE_KEY = "discount-preview-cache.v1";
const DISCOUNT_PREVIEW_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const DISCOUNT_PREVIEW_CACHE_MAX_ENTRIES = 10;

const hashString = (input) => {
  if (!input) {
    return "0";
  }

  let hash = 0;
  for (let index = 0; index < input.length; index += 1) {
    hash = (hash << 5) - hash + input.charCodeAt(index);
    hash |= 0; // Convert to 32bit integer
  }
  return hash.toString(16);
};

const buildDiscountPreviewCacheSignature = (payload = {}) => {
  const fingerprint = buildDiscountPreviewFingerprint(payload);
  if (!fingerprint) {
    return null;
  }

  const panelCount = Array.isArray(payload.panels)
    ? payload.panels.filter((panel) => panel && panel.id).length
    : 0;

  return {
    key: `discount-preview:${hashString(fingerprint)}:${panelCount}`,
    fingerprint,
  };
};

const readDiscountPreviewCache = () => {
  if (typeof window === "undefined" || !window.sessionStorage) {
    return {};
  }
  try {
    const raw = window.sessionStorage.getItem(DISCOUNT_PREVIEW_CACHE_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (error) {
    return {};
  }
};

const writeDiscountPreviewCache = (cache) => {
  if (typeof window === "undefined" || !window.sessionStorage) {
    return;
  }
  try {
    window.sessionStorage.setItem(DISCOUNT_PREVIEW_CACHE_STORAGE_KEY, JSON.stringify(cache));
  } catch (error) {
    // best-effort cache; ignore quota errors
  }
};

const getCachedDiscountPreview = (signature) => {
  if (!signature) {
    return null;
  }

  const cache = readDiscountPreviewCache();
  const entry = cache[signature.key];
  if (!entry) {
    return null;
  }

  if (entry.fingerprint !== signature.fingerprint) {
    delete cache[signature.key];
    writeDiscountPreviewCache(cache);
    return null;
  }

  if (!entry.timestamp || (Date.now() - entry.timestamp) > DISCOUNT_PREVIEW_CACHE_TTL_MS) {
    delete cache[signature.key];
    writeDiscountPreviewCache(cache);
    return null;
  }

  return entry.value || null;
};

const persistDiscountPreviewCache = (signature, value) => {
  if (!signature || !value) {
    return;
  }

  const cache = readDiscountPreviewCache();
  cache[signature.key] = {
    fingerprint: signature.fingerprint,
    value,
    timestamp: Date.now(),
  };

  const cacheEntries = Object.entries(cache);
  if (cacheEntries.length > DISCOUNT_PREVIEW_CACHE_MAX_ENTRIES) {
    cacheEntries
      .sort((a, b) => (a[1].timestamp || 0) - (b[1].timestamp || 0))
      .slice(0, cacheEntries.length - DISCOUNT_PREVIEW_CACHE_MAX_ENTRIES)
      .forEach(([key]) => {
        delete cache[key];
      });
  }

  writeDiscountPreviewCache(cache);
};

const inFlightDiscountPreviewRequests = new Map();

const fetchDiscountPreviewNetwork = async (orderPayload = {}) => {
  const panels = Array.isArray(orderPayload.panels) ? orderPayload.panels.filter(Boolean) : [];
  const panelChunks = chunkPanelsForPreview(panels);
  const responses = [];

  for (const chunk of panelChunks) {
    const chunkPayload = {
      ...orderPayload,
      panels: chunk,
      visitInfo: {
        ...(orderPayload.visitInfo || {}),
        webNumberOfPanels: chunk.length,
      },
    };

    const result = await NewOrderService.previewDiscounts(chunkPayload);
    if (!result.status) {
      throw new Error(result.message || "Unable to preview discounts");
    }

    responses.push(result.data);
  }

  return mergeDiscountPreviewResponses(responses, panels.length);
};

const executeDiscountPreviewRequest = (orderPayload, cacheSignature) => {
  if (!cacheSignature) {
    return fetchDiscountPreviewNetwork(orderPayload);
  }

  const inFlightRequest = inFlightDiscountPreviewRequests.get(cacheSignature.key);
  if (inFlightRequest && inFlightRequest.fingerprint === cacheSignature.fingerprint) {
    return inFlightRequest.promise;
  }

  const requestPromise = fetchDiscountPreviewNetwork(orderPayload).finally(() => {
    const currentRequest = inFlightDiscountPreviewRequests.get(cacheSignature.key);
    if (currentRequest && currentRequest.promise === requestPromise) {
      inFlightDiscountPreviewRequests.delete(cacheSignature.key);
    }
  });

  inFlightDiscountPreviewRequests.set(cacheSignature.key, {
    fingerprint: cacheSignature.fingerprint,
    promise: requestPromise,
  });

  return requestPromise;
};

export function* onFetchNewOrderTest() {
  yield takeLatest(FETCH_NEW_ORDER_TESTS_REQUEST, function* (action) {
    try {
      const { meta } = action || {};
      let cachedList = yield select((state) => state.newOrder?.list || []);
      if ((!cachedList || cachedList.length === 0) && !(meta && meta.force)) {
        const storageCached = yield call(readCachedNewOrderTests);
        if (storageCached && Array.isArray(storageCached) && storageCached.length > 0) {
          cachedList = storageCached;
        }
      }
      if (cachedList && cachedList.length > 0 && !(meta && meta.force)) {
        yield put(fetchNewOrderTestSuccess(cachedList));
        return;
      }
      if (meta && meta.force) {
        yield call(clearCachedNewOrderTests);
      }
      const result = yield call(NewOrderService.index);
      if (result.status) {
        yield put(fetchNewOrderTestSuccess(result.data));
        yield call(writeCachedNewOrderTests, result.data);
      } else {
        yield put(fetchNewOrderTestFailure(result.message));
      }
    } catch (error) {
      yield put(fetchNewOrderTestFailure(error.message));
    }
  });
}

export function* onFetchLocationLookUp() {
  yield takeLatest(FETCH_LOCATION_LOOKUP_REQUEST, function* () {
    try {
      const result = yield call(NewOrderService.getLocations);
      if (result.status) {
        yield put(fetchLocationLookUpSuccess(result.data));
      } else {
        yield put(fetchLocationLookUpFailure(result.message));
      }
    } catch (error) {
      yield put(fetchLocationLookUpFailure(error.message));
    }
  });
}

export function* onFetchzone() {
  yield takeLatest(FETCH_ZONE_REQUEST, function* (parentid) {
    try {
      const result = yield call(NewOrderService.getzone, parentid);
      if (result.status) {
        yield put(fetchzoneSuccess(result.data));
      } else {
        yield put(fetchzoneFailure(result.message));
      }
    } catch (error) {
      yield put(fetchzoneFailure(error.message));
    }
  });
}

export function* onFetchworeda() {
  yield takeLatest(FETCH_WOREDA_REQUEST, function* (parentid) {
    try {
      const result = yield call(NewOrderService.getzone, parentid);
      if (result.status) {
        yield put(fetchworedaSuccess(result.data));
      } else {
        yield put(fetchworedaFailure(result.message));
      }
    } catch (error) {
      yield put(fetchworedaFailure(error.message));
    }
  });
}

export function* onFetchExistingPatients() {
  yield takeLatest(FETCH_EXISTING_NEW_ORDER_PATIENT_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(NewOrderService.searchpatient, payload);
      if (result.status === false) {
        // message.error("Something went wrong");
        yield put(fetchNewOrderPatientsFailure(result.message));
      } else if (result.status === true && result.data.length === 0) {
        // message.warning("Not found");
        yield put(fetchNewOrderPatientsFailure("Not found"));
      } else {
        yield put(fetchNewOrderPatientsSuccess(result.data));
      }
    } catch (error) {
      yield put(fetchNewOrderPatientsFailure(error.message));
    }
  });
}

export function* onCreateNewOrder() {
  yield takeLatest(CREATE_NEW_ORDER_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(NewOrderService.create, payload.newOrder);
      if (result.status && doesThisUserIsSrd()) {
        yield put(createNewOrderSuccess(result.data));
        let orderInformation = result.data;
        let sampleId = orderInformation.visitInfo.webSampleId;
        openPdf(sampleId, true, "trf");
      } else if (result.status && getCurrentRole() !== ROLE_GUEST) {
        let orderInformation = result.data;
        let sampleId = orderInformation.visitInfo.webSampleId;
        // payload.sampleManagementData.sample_ID = sampleId;
        // sendSampleToSampleManagement(payload.sampleManagementData);
        yield put(createNewOrderSuccess(result.data));
        openPdf(sampleId, payload.doesThePdfAllowedToBeOpened, payload.pdfType);
      } else if (result.status && getCurrentRole() === ROLE_GUEST) {
        yield put(createNewOrderSuccess(result.data));
        if (result.data.ledgerInfo[0].booking_id != null) {
          message.success("Booking Created successfully!")
        }
      } else if (result.code === 409 && result.error === 'discount_limit_reached') {
        const limitScope = 'STANDARD_DISCOUNT';
        message.error(result.message || 'Standard discount usage limit reached. Refresh the discount preview or remove exhausted discounts.');
        yield put(createNewOrderFailure(result.message));
        yield put(discountLimitReached({ ...result, limitScope }));
      } else if (result.code === UNPROCESSABLE_ENTITY_CODE) {
        message.error("Check your inputs please");
        yield put(setNewOrderValidationErrors(result.data.errors));
        yield put(createNewOrderFailure(result.message));
      } else {
        message.error("Something went wrong");
        yield put(createNewOrderFailure(result.message));
      }

    } catch (error) {
      message.error("Something went wrong");
      yield put(createNewOrderFailure(error.message));
    }
  });
}


export function* onCreateNewOrderByDoctor() {
  yield takeLatest(CREATE_NEW_ORDER_BY_DOCTOR_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(NewOrderService.create, payload.newOrder);
      if (result.status && getCurrentRole() !== ROLE_GUEST) {
        let orderInformation = result.data;
        let sampleId = orderInformation.visitInfo.webSampleId;
        payload.sampleManagementData.sample_ID = sampleId;
        sendSampleToSampleManagement(payload.sampleManagementData);
        yield put(createNewOrderByDoctorSuccess(result.data));
        message.success("Order sent successfully!")
        // openPdf(sampleId, payload.doesThePdfAllowedToBeOpened, "forCash" );
      } else if (result.status && getCurrentRole() === ROLE_GUEST) {
        yield put(createNewOrderSuccess(result.data));
      } else if (result.code === UNPROCESSABLE_ENTITY_CODE) {
        message.error("Check your inputs please");
        yield put(setNewOrderValidationErrors(result.data.errors));
        yield put(createNewOrderFailure(result.message));
      } else {
        message.error("Something went wrong");
        yield put(createNewOrderFailure(result.message));
      }

    } catch (error) {
      message.error("Something went wrong");
      yield put(createNewOrderFailure(error.message));
    }
  });
}

export function* onFetchDiscountPreview() {
  yield takeLatest(FETCH_DISCOUNT_PREVIEW_REQUEST, function* ({ payload }) {
    try {
      const orderPayload = payload || {};
      const panels = Array.isArray(orderPayload.panels) ? orderPayload.panels.filter(Boolean) : [];

      if (panels.length === 0) {
        yield put(fetchDiscountPreviewSuccess(null));
        return;
      }

      const cacheSignature = buildDiscountPreviewCacheSignature(orderPayload);
      if (cacheSignature) {
        const cachedPreview = yield call(getCachedDiscountPreview, cacheSignature);
        if (cachedPreview) {
          yield put(fetchDiscountPreviewSuccess(cachedPreview));
          return;
        }
      }

      const mergedPreview = yield call(executeDiscountPreviewRequest, orderPayload, cacheSignature);
      if (cacheSignature) {
        yield call(persistDiscountPreviewCache, cacheSignature, mergedPreview);
      }
      yield put(fetchDiscountPreviewSuccess(mergedPreview));
    } catch (error) {
      message.error(error?.message || "Unable to preview discounts");
      yield put(fetchDiscountPreviewFailure(error?.message || "Unable to preview discounts"));
    }
  });
}


export function* onUpdateCsOrFsNumber() {
  yield takeLatest(UPDATE_CS_OR_FS_NUMBER_REQUEST, function* ({ payload }) {
    try {
      // console.log('--->saga payload',payload);
      const result = yield call(
        NewOrderService.updateCacheRegister,
        payload.payload
      );
      if (result.status) {
        yield put(updateCsOrFsNumberSuccess(result.data));
        openPdf(payload.payload.generatedSampleId, true, payload.pdfType);
      } else {
        message.error(result.message || "Error updating cash register machine");
        yield put(updateCsOrFsNumberFailure(result.message));
      }
    } catch (error) {
      message.error(error?.message || "Error updating cash register machine");
      yield put(updateCsOrFsNumberFailure(error.message));
    }
  });
}


export function* onUpdateNewOrder() {
  yield takeLatest(UPDATE_NEW_ORDER_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(
        NewOrderService.update,
        payload.id,
        payload
      );
      if (result.status) {
        message.success("Updated");
        yield put(updateNewOrderSuccess(result.data));
      } else if (result.code === UNPROCESSABLE_ENTITY_CODE) {
        yield put(setNewOrderValidationErrors(result.data.errors));
        yield put(updateNewOrderFailure(result.message));
      } else {
        yield put(updateNewOrderFailure(result.message));
      }
    } catch (error) {
      yield put(updateNewOrderFailure(error.message));
    }
  });
}

export default function* rootSaga() {
  yield all([
    fork(onFetchzone),
    fork(onFetchworeda),
    fork(onFetchNewOrderTest),
    fork(onFetchLocationLookUp),
    fork(onFetchExistingPatients),
    fork(onCreateNewOrder),
    fork(onUpdateCsOrFsNumber),
    fork(onUpdateNewOrder),
    fork(onCreateNewOrderByDoctor),
    fork(onFetchDiscountPreview)
  ]);
}
